import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TimeSelect from './TimeSelect';

const API = 'https://api.cpms-srlkevcharge.com';

const DAYS = [
  { key: 'Mon', label: 'Mon' },
  { key: 'Tue', label: 'Tue' },
  { key: 'Wed', label: 'Wed' },
  { key: 'Thu', label: 'Thu' },
  { key: 'Fri', label: 'Fri' },
  { key: 'Sat', label: 'Sat' },
  { key: 'Sun', label: 'Sun' },
];

const IconTag = () => (
  <svg width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path d="M20.59 13.41L11 3.82V3H3v8l.82.82L13.41 20.6a2 2 0 002.83 0l4.34-4.34a2 2 0 000-2.83z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);
const IconPlus = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const IconTrash = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

function StatusPill({ status }) {
  const s = (status || 'draft').toLowerCase();
  return <span className={`tariff-status-pill tariff-status-${s}`}>{status}</span>;
}

function DayPicker({ value, onChange }) {
  const selectedDays = value ? value.split(',').map(d => d.trim()) : [];

  const toggleDay = (dayKey) => {
    const isSelected = selectedDays.includes(dayKey);
    const updated = isSelected
      ? selectedDays.filter(d => d !== dayKey)
      : [...selectedDays, dayKey];
    onChange(updated.length > 0 ? updated.join(',') : '');
  };

  return (
    <div className="day-picker">
      {DAYS.map(day => (
        <button
          type="button"
          key={day.key}
          className={`day-pill ${selectedDays.includes(day.key) ? 'selected' : ''}`}
          onClick={() => toggleDay(day.key)}
          title={day.key}
        >
          {day.label}
        </button>
      ))}
    </div>
  );
}

export default function WizardStepPrice({
  schedules, setSchedules, unresolvedConflicts, onResolveConflict, gapWarnings, tariffLookup
}) {
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDays, setOpenDays] = useState({});
  const [openDates, setOpenDates] = useState({});

  useEffect(() => {
    axios.get(`${API}/tariffs/`)
      .then(res => setTariffs(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const addSchedule = () => {
    setSchedules(prev => [
      ...prev,
      {
        tempId: Date.now(),
        tariff_id: '',
        start_time: '',
        end_time: '',
        days_of_week: '',
        effective_start_date: '',
        effective_end_date: '',
        priority: 0,
      }
    ]);
  };

  const removeSchedule = (tempId) => {
    setSchedules(prev => prev.filter(s => s.tempId !== tempId));
  };

  const updateSchedule = (tempId, field, value) => {
    setSchedules(prev => prev.map(s => s.tempId === tempId ? { ...s, [field]: value } : s));
  };

  const toggleDaysSection = (tempId) => {
    setOpenDays(prev => ({ ...prev, [tempId]: !prev[tempId] }));
  };

  const toggleDatesSection = (tempId) => {
    setOpenDates(prev => ({ ...prev, [tempId]: !prev[tempId] }));
  };

  const formatPrice = (tariff) => {
    if (!tariff.price_components || tariff.price_components.length === 0) return 'No pricing set';
    const first = tariff.price_components[0];
    const suffix = tariff.price_components.length > 1 ? ` +${tariff.price_components.length - 1} more` : '';
    return `${tariff.currency} ${first.price_per_unit} / ${first.charging_unit}${suffix}`;
  };

  const isInConflict = (tempId) => {
    return (unresolvedConflicts || []).some(c => c.tempIdA === tempId || c.tempIdB === tempId);
  };

  if (loading) return <div className="loading">Loading tariffs...</div>;

  return (
    <>
      <div className="card" style={{ padding: 28, marginBottom: 20 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--gray-800)', marginBottom: 4 }}>
          Apply Tariffs to this Station
        </h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Add one or more tariffs. Use different time windows, days, or date ranges to set different rates for different periods. Leave everything blank for a tariff that always applies. This step is optional.
        </p>
      </div>

      {(unresolvedConflicts || []).map((conflict, i) => (
        <div className="collision-choice-box" key={i}>
          <div className="collision-choice-message">{conflict.message}</div>
          <div className="collision-choice-buttons">
            <button
              type="button"
              className="collision-choice-btn"
              onClick={() => onResolveConflict(conflict.tempIdA, conflict.tempIdB)}
            >
              Give priority to {tariffLookup[schedules.find(s => s.tempId === conflict.tempIdA)?.tariff_id]}
            </button>
            <button
              type="button"
              className="collision-choice-btn"
              onClick={() => onResolveConflict(conflict.tempIdB, conflict.tempIdA)}
            >
              Give priority to {tariffLookup[schedules.find(s => s.tempId === conflict.tempIdB)?.tariff_id]}
            </button>
          </div>
        </div>
      ))}

      {gapWarnings && gapWarnings.length > 0 && (unresolvedConflicts || []).length === 0 && (
        <div className="schedule-gap-banner">
          <div className="schedule-gap-banner-title">⚠ Some periods have no tariff assigned</div>
          <ul className="schedule-gap-banner-list">
            {gapWarnings.slice(0, 6).map((gap, i) => <li key={i}>{gap}</li>)}
            {gapWarnings.length > 6 && <li>...and {gapWarnings.length - 6} more</li>}
          </ul>
        </div>
      )}

      {tariffs.length === 0 ? (
        <div className="card empty-state">
          <IconTag style={{ opacity: .3, marginBottom: 12 }} />
          <h3>No tariffs available yet</h3>
          <p>You can skip this step and apply a tariff later once one has been created.</p>
        </div>
      ) : (
        <>
          {schedules.length === 0 && (
            <div className="card empty-state" style={{ marginBottom: 16 }}>
              <h3>No tariffs added yet</h3>
              <p>Add a tariff below, or skip this step entirely.</p>
            </div>
          )}

          {schedules.map((schedule, index) => {
            const selectedTariff = tariffs.find(t => String(t.id) === String(schedule.tariff_id));
            const showDays = !!openDays[schedule.tempId] || !!schedule.days_of_week;
            const showDates = !!openDates[schedule.tempId] || !!(schedule.effective_start_date || schedule.effective_end_date);

            return (
              <div
                className={`entity-card ${isInConflict(schedule.tempId) ? 'has-conflict' : ''}`}
                key={schedule.tempId}
              >
                <div className="entity-card-header">
                  <span className="entity-card-title">
                    Tariff {index + 1}
                    {selectedTariff && <span className="entity-card-badge">{selectedTariff.name}</span>}
                  </span>
                  <button
                    type="button"
                    className="entity-remove-btn"
                    onClick={() => removeSchedule(schedule.tempId)}
                  >
                    <IconTrash />
                  </button>
                </div>

                <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
                  <div className="form-group">
                    <label>Select Tariff</label>
                    <select
                      value={schedule.tariff_id}
                      onChange={(e) => updateSchedule(schedule.tempId, 'tariff_id', e.target.value)}
                    >
                      <option value="">Select...</option>
                      {tariffs.map(t => (
                        <option key={t.id} value={t.id}>{t.name} — {t.organisation_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Start Time</label>
                    <TimeSelect
                      value={schedule.start_time}
                      onChange={(val) => updateSchedule(schedule.tempId, 'start_time', val)}
                    />
                  </div>
                  <div className="form-group">
                    <label>End Time</label>
                    <TimeSelect
                      value={schedule.end_time}
                      onChange={(val) => updateSchedule(schedule.tempId, 'end_time', val)}
                    />
                  </div>
                </div>

                <div className="advanced-toggle-row">
                  <label className="advanced-toggle">
                    <input
                      type="checkbox"
                      checked={showDays}
                      onChange={() => toggleDaysSection(schedule.tempId)}
                    />
                    Specific days of the week
                  </label>
                  <label className="advanced-toggle">
                    <input
                      type="checkbox"
                      checked={showDates}
                      onChange={() => toggleDatesSection(schedule.tempId)}
                    />
                    Specific date range
                  </label>
                </div>

                {showDays && (
                  <div className="advanced-section">
                    <div className="advanced-section-label">Applies on these days (leave all unselected for every day)</div>
                    <DayPicker
                      value={schedule.days_of_week}
                      onChange={(val) => updateSchedule(schedule.tempId, 'days_of_week', val)}
                    />
                  </div>
                )}

                {showDates && (
                  <div className="advanced-section">
                    <div className="advanced-section-label">Effective date range (leave end date blank for ongoing)</div>
                    <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                      <div className="form-group">
                        <label>Start Date</label>
                        <input
                          type="date"
                          value={schedule.effective_start_date}
                          onChange={(e) => updateSchedule(schedule.tempId, 'effective_start_date', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>End Date</label>
                        <input
                          type="date"
                          value={schedule.effective_end_date}
                          onChange={(e) => updateSchedule(schedule.tempId, 'effective_end_date', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {selectedTariff && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <StatusPill status={selectedTariff.status} />
                    <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--gray-800)' }}>
                      {formatPrice(selectedTariff)}
                    </span>
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                      {schedule.days_of_week || 'All days'} · {schedule.start_time && schedule.end_time ? `${schedule.start_time}–${schedule.end_time}` : 'All hours'}
                      {(schedule.effective_start_date || schedule.effective_end_date) &&
                        ` · From ${schedule.effective_start_date || 'now'} ${schedule.effective_end_date ? `to ${schedule.effective_end_date}` : '(ongoing)'}`}
                    </span>
                  </div>
                )}
              </div>
            );
          })}

          <button type="button" className="add-entity-btn" onClick={addSchedule}>
            <IconPlus /> Add Tariff
          </button>
        </>
      )}
    </>
  );
}